/**
 * PaginasManager.js
 *
 * Pantalla del sidebar: lista de sitios, creación desde plantilla, importación y
 * leads capturados. Abre el SiteBuilder a pantalla completa.
 */

import React, { useState, useEffect, useCallback } from 'react';
import SiteBuilder from './builder/SiteBuilder';
import { downloadJson, readJsonFile } from './builder/builderModel';
import API_URL from '../../config/api';
import './builder/builder.css';

function authHeaders() {
  const token = sessionStorage.getItem('token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
    : { 'Content-Type': 'application/json' };
}

async function api(path, options) {
  // El frontend es un Static Site en un dominio distinto al backend, así que
  // todas las rutas van prefijadas con API_URL como en el resto del proyecto.
  const r = await fetch(API_URL + path, Object.assign({ headers: authHeaders() }, options));
  const text = await r.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch (e) { json = null; }
  if (!r.ok) throw new Error((json && json.error) || 'Error ' + r.status);
  return json;
}

export default function PaginasManager() {
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [leadsFor, setLeadsFor] = useState(null);

  // Sitios estáticos subidos como .zip (2026-08-08) -- segunda vía junto al
  // editor visual de arriba, viven en su propia tabla/endpoints
  // (/api/pos/sites/static) porque no comparten nada con `sites`: no hay
  // "content" editable, el .zip completo ES el sitio.
  const [staticSites, setStaticSites] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [replacingStatic, setReplacingStatic] = useState(null);

  // Dominios propios (2026-08-09) -- aplica tanto a sitios del editor
  // (targetKind 'builder') como a sitios subidos en .zip (targetKind
  // 'static'), por eso el modal se abre pasándole el tipo+id del sitio en
  // cuestión en vez de vivir separado por tipo.
  const [domainsFor, setDomainsFor] = useState(null);

  const notify = (text, kind) => {
    setMsg({ text, kind: kind || 'info' });
    window.setTimeout(() => setMsg(null), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, ss] = await Promise.all([
        api('/api/pos/sites'),
        api('/api/pos/site-templates').catch(() => []),
        api('/api/pos/sites/static').catch(() => [])
      ]);
      setSites(s || []);
      setTemplates(t || []);
      setStaticSites(ss || []);
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStaticStatus = async (site) => {
    try {
      const next = site.status === 'active' ? 'disabled' : 'active';
      await api('/api/pos/sites/static/' + site.id, { method: 'PATCH', body: JSON.stringify({ status: next }) });
      notify(next === 'active' ? 'Sitio activado' : 'Sitio pausado', 'ok');
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const removeStatic = async (site) => {
    if (!window.confirm('¿Eliminar "' + site.name + '"? Se borran todos sus archivos y no se puede deshacer.')) return;
    try {
      await api('/api/pos/sites/static/' + site.id, { method: 'DELETE' });
      notify('Sitio eliminado', 'ok');
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const remove = async (site) => {
    if (!window.confirm('¿Eliminar "' + site.name + '"? Se borran todas sus páginas y no se puede deshacer.')) return;
    try {
      await api('/api/pos/sites/' + site.id, { method: 'DELETE' });
      notify('Sitio eliminado', 'ok');
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const duplicate = async (site) => {
    try {
      const data = await api('/api/pos/sites/' + site.id + '/export');
      const r = await api('/api/pos/sites/import', {
        method: 'POST',
        body: JSON.stringify({ data, name: site.name + ' (copia)' })
      });
      notify('Copia creada en /p/' + r.slug, 'ok');
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const exportSite = async (site) => {
    try {
      const data = await api('/api/pos/sites/' + site.id + '/export');
      downloadJson(data, site.slug + '.marketos.json');
    } catch (e) { notify(e.message, 'error'); }
  };

  const importSite = async (file) => {
    try {
      const data = await readJsonFile(file);
      const r = await api('/api/pos/sites/import', { method: 'POST', body: JSON.stringify({ data }) });
      notify('Sitio importado' + (r.warnings?.length ? ' — ' + r.warnings.join(' · ') : ''), 'ok');
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const unpublish = async (site) => {
    try {
      await api('/api/pos/sites/' + site.id + '/unpublish', { method: 'POST' });
      notify('Sitio despublicado', 'ok');
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  if (editing) {
    return <SiteBuilder siteId={editing} onExit={() => { setEditing(null); load(); }} />;
  }

  return (
    <div className="pm">
      <header className="pm-head">
        <div>
          <h2>Páginas web</h2>
          <p>Construye landings, páginas de captación y micrositios. Cada palabra se edita directamente sobre el diseño.</p>
        </div>
        <div className="pm-head-actions">
          <label className="pm-btn ghost">
            Importar archivo
            <input type="file" accept="application/json,.json" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) importSite(f); e.target.value = ''; }} />
          </label>
          <button type="button" className="pm-btn primary" onClick={() => setShowNew(true)}>+ Nuevo sitio</button>
        </div>
      </header>

      {msg ? <div className={'bld-status ' + msg.kind}>{msg.text}</div> : null}

      {loading ? (
        <div className="pm-loading">Cargando…</div>
      ) : sites.length === 0 ? (
        <div className="pm-blank">
          <h3>Todavía no tienes ninguna página</h3>
          <p>Empieza desde una plantilla lista y cámbiale los textos, o importa un sitio que ya hayas exportado desde otra cuenta.</p>
          <button type="button" className="pm-btn primary" onClick={() => setShowNew(true)}>Crear mi primera página</button>
        </div>
      ) : (
        <div className="pm-grid">
          {sites.map((s) => (
            <article className="pm-card" key={s.id}>
              <div className="pm-card-top">
                <h3>{s.name}</h3>
                <span className={'bld-chip ' + (s.status === 'published' ? 'ok' : '')}>
                  {s.status === 'published' ? 'Publicado' : 'Borrador'}
                </span>
              </div>
              <code className="pm-url" title={API_URL + '/p/' + s.slug}>/p/{s.slug}</code>
              <div className="pm-meta">
                <span>{s.pages_count} {s.pages_count === 1 ? 'página' : 'páginas'}</span>
                <span>·</span>
                <button type="button" className="pm-link" onClick={() => setLeadsFor(s)}>
                  {s.leads_count} {s.leads_count === 1 ? 'lead' : 'leads'}
                </button>
              </div>
              <div className="pm-actions">
                <button type="button" className="pm-btn primary sm" onClick={() => setEditing(s.id)}>Editar</button>
                {s.status === 'published' && (
                  <a className="pm-btn sm" href={API_URL + '/p/' + s.slug} target="_blank" rel="noopener noreferrer">Ver ↗</a>
                )}
                <div className="pm-more">
                  <button type="button" className="pm-btn sm">⋯</button>
                  <div className="pm-more-pop">
                    <button type="button" onClick={() => duplicate(s)}>Duplicar</button>
                    <button type="button" onClick={() => exportSite(s)}>Exportar</button>
                    <button type="button" onClick={() => setDomainsFor({ kind: 'builder', id: s.id, name: s.name })}>Dominio propio</button>
                    {s.status === 'published' && <button type="button" onClick={() => unpublish(s)}>Despublicar</button>}
                    <button type="button" className="danger" onClick={() => remove(s)}>Eliminar</button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showNew && (
        <NewSiteModal
          templates={templates}
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); load(); setEditing(id); }}
          onRequested={(message) => { setShowNew(false); notify(message || 'La creación quedó pendiente de aprobación de MARKETOS.', 'info'); }}
          onError={(m) => notify(m, 'error')}
        />
      )}

      {leadsFor && <LeadsModal site={leadsFor} onClose={() => setLeadsFor(null)} />}

      <header className="pm-head" style={{ marginTop: 40 }}>
        <div>
          <h2>Sitios subidos (.zip)</h2>
          <p>Sube un sitio ya terminado (HTML/CSS/JS/imágenes) y se publica tal cual, sin pasar por el editor. Para reemplazarlo, vuelve a subir el .zip.</p>
        </div>
        <div className="pm-head-actions">
          <button type="button" className="pm-btn primary" onClick={() => setShowUpload(true)}>+ Subir sitio (.zip)</button>
        </div>
      </header>

      {staticSites.length === 0 ? (
        <div className="pm-blank">
          <h3>Todavía no subiste ningún sitio</h3>
          <p>Diseña la landing donde quieras (por ejemplo, aquí mismo con Claude), expórtala como .zip con un index.html en la raíz, y súbela.</p>
        </div>
      ) : (
        <div className="pm-grid">
          {staticSites.map((s) => (
            <article className="pm-card" key={'static-' + s.id}>
              <div className="pm-card-top">
                <h3>{s.name}</h3>
                <span className={'bld-chip ' + (s.status === 'active' ? 'ok' : '')}>
                  {s.status === 'active' ? 'Activo' : 'Pausado'}
                </span>
              </div>
              <code className="pm-url" title={API_URL + '/s/' + s.slug}>/s/{s.slug}</code>
              <div className="pm-meta">
                <span>{s.file_count} {s.file_count === 1 ? 'archivo' : 'archivos'}</span>
                <span>·</span>
                <span>{(s.total_bytes / 1024 / 1024).toFixed(1)} MB</span>
              </div>
              <div className="pm-actions">
                <button type="button" className="pm-btn primary sm" onClick={() => setReplacingStatic(s)}>Reemplazar</button>
                {s.status === 'active' && (
                  <a className="pm-btn sm" href={API_URL + '/s/' + s.slug} target="_blank" rel="noopener noreferrer">Ver ↗</a>
                )}
                <div className="pm-more">
                  <button type="button" className="pm-btn sm">⋯</button>
                  <div className="pm-more-pop">
                    <button type="button" onClick={() => toggleStaticStatus(s)}>{s.status === 'active' ? 'Pausar' : 'Activar'}</button>
                    <button type="button" onClick={() => setDomainsFor({ kind: 'static', id: s.id, name: s.name })}>Dominio propio</button>
                    <button type="button" className="danger" onClick={() => removeStatic(s)}>Eliminar</button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadZipModal
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); load(); }}
          onError={(m) => notify(m, 'error')}
        />
      )}

      {replacingStatic && (
        <UploadZipModal
          replaceId={replacingStatic.id}
          replaceName={replacingStatic.name}
          onClose={() => setReplacingStatic(null)}
          onDone={() => { setReplacingStatic(null); load(); }}
          onError={(m) => notify(m, 'error')}
        />
      )}

      {domainsFor && (
        <DomainsModal
          target={domainsFor}
          onClose={() => setDomainsFor(null)}
          onError={(m) => notify(m, 'error')}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function DomainsModal({ target, onClose, onError }) {
  const [all, setAll] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api('/api/pos/domains').then(setAll).catch((e) => { onError(e.message); setAll([]); });
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const mine = (all || []).filter((d) => d.target_kind === target.kind && d.target_id === target.id);
  const cnameTarget = (() => { try { return new URL(API_URL).host; } catch (e) { return API_URL; } })();

  const add = async () => {
    if (!newDomain.trim()) { onError('Escribe un dominio, ej: midominio.com'); return; }
    setBusy(true);
    try {
      await api('/api/pos/domains', {
        method: 'POST',
        body: JSON.stringify({ domain: newDomain.trim(), targetKind: target.kind, targetId: target.id })
      });
      setNewDomain('');
      load();
    } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api('/api/pos/domains/' + id, { method: 'DELETE' });
      load();
    } catch (e) { onError(e.message); }
  };

  return (
    <div className="bld-modal" onClick={onClose}>
      <div className="bld-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3>Dominio propio de "{target.name}"</h3>

        <label>Agregar dominio</label>
        <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="midominio.com" />
        <small>
          Antes de agregarlo acá: en tu proveedor de dominio (ej. Porkbun), crea un registro <b>CNAME</b> que
          apunte tu dominio hacia <code>{cnameTarget}</code>. Luego avísale a soporte para que lo agregue también
          en "Custom Domains" del servidor — sin ese paso, aunque lo registres acá, el navegador no va a
          conectarse a este servidor cuando alguien visite tu dominio.
        </small>

        {all === null ? (
          <p>Cargando…</p>
        ) : mine.length === 0 ? (
          <p className="pm-empty">Todavía no tiene ningún dominio propio — se sigue viendo solo en la dirección compartida.</p>
        ) : (
          <div className="pm-table-wrap">
            <table className="pm-table">
              <thead><tr><th>Dominio</th><th></th></tr></thead>
              <tbody>
                {mine.map((d) => (
                  <tr key={d.id}>
                    <td>{d.domain}</td>
                    <td><button type="button" className="danger" onClick={() => remove(d.id)}>Quitar</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="bld-modal-actions">
          <button type="button" onClick={onClose}>Cerrar</button>
          <button type="button" className="primary" onClick={add} disabled={busy}>
            {busy ? 'Agregando…' : 'Agregar dominio'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function NewSiteModal({ templates, onClose, onCreated, onRequested, onError }) {
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const system = templates.filter((t) => t.isSystem && t.scope === 'site');
  const mine = templates.filter((t) => !t.isSystem && t.scope === 'site');

  useEffect(() => {
    const recommended = templates.find((template) => template.isSystem && template.scope === 'site');
    if (recommended?.id) setTemplateId((current) => current || String(recommended.id));
  }, [templates]);

  const create = async () => {
    if (!name.trim()) { onError('Ponle un nombre al sitio'); return; }
    setBusy(true);
    try {
      const r = await api('/api/pos/sites', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), templateId: templateId || undefined })
      });
      if (r?.approval_required) {
        onRequested(r.message);
        return;
      }
      onCreated(r.id);
    } catch (e) {
      onError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="bld-modal" onClick={onClose}>
      <div className="bld-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3>Nuevo sitio</h3>
        <label>Nombre</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Landing de promociones" autoFocus />
        <small>La dirección pública se genera a partir del nombre y la puedes cambiar después.</small>

        <label>Empezar desde</label>
        <div className="pm-tpls">
          <button type="button" className={!templateId ? 'on' : ''} onClick={() => setTemplateId('')}>
            <strong>En blanco · avanzado</strong><span>Para construir todo desde cero</span>
          </button>
          {system.map((t, index) => (
            <button type="button" key={t.id} className={String(templateId) === String(t.id) ? 'on' : ''}
              onClick={() => setTemplateId(t.id)}>
              <strong>{t.name}{index === 0 ? ' · recomendado' : ''}</strong><span>{t.description}</span>
            </button>
          ))}
          {mine.map((t) => (
            <button type="button" key={t.id} className={String(templateId) === String(t.id) ? 'on' : ''}
              onClick={() => setTemplateId(t.id)}>
              <strong>★ {t.name}</strong><span>{t.description || 'Plantilla propia'}</span>
            </button>
          ))}
        </div>

        <div className="bld-modal-actions">
          <button type="button" onClick={onClose}>Cancelar</button>
          <button type="button" className="primary" onClick={create} disabled={busy}>
            {busy ? 'Creando…' : 'Crear y editar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadZipModal({ replaceId, replaceName, onClose, onDone, onError }) {
  const [name, setName] = useState(replaceName || '');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const isReplace = !!replaceId;

  const submit = async () => {
    if (!file) { onError('Elige un archivo .zip'); return; }
    if (!isReplace && !name.trim()) { onError('Ponle un nombre al sitio'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('zip', file);
      if (!isReplace) form.append('name', name.trim());

      const token = sessionStorage.getItem('token');
      const url = API_URL + (isReplace ? '/api/pos/sites/static/' + replaceId + '/replace' : '/api/pos/sites/static');
      const r = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: 'Bearer ' + token } : {},
        body: form
      });
      const text = await r.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch (e) { json = null; }
      if (!r.ok) throw new Error((json && json.error) || 'Error ' + r.status);

      if (json?.warnings?.length) onError('Subido con avisos: ' + json.warnings.join(' · '));
      onDone();
    } catch (e) {
      onError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="bld-modal" onClick={onClose}>
      <div className="bld-modal-box" onClick={(e) => e.stopPropagation()}>
        <h3>{isReplace ? 'Reemplazar "' + replaceName + '"' : 'Subir sitio (.zip)'}</h3>
        {!isReplace && (
          <>
            <label>Nombre</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Landing promo verano" autoFocus />
          </>
        )}
        <label>Archivo .zip</label>
        <input type="file" accept="application/zip,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <small>Debe traer un index.html en la raíz (o dentro de la única carpeta que contiene). Máximo ~60MB descomprimido.</small>
        {isReplace && <small>Esto reemplaza TODOS los archivos actuales del sitio, sin deshacer.</small>}

        <div className="bld-modal-actions">
          <button type="button" onClick={onClose}>Cancelar</button>
          <button type="button" className="primary" onClick={submit} disabled={busy}>
            {busy ? 'Subiendo…' : isReplace ? 'Reemplazar' : 'Subir y publicar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadsModal({ site, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api('/api/pos/sites/' + site.id + '/submissions').then(setRows).catch(() => setRows([]));
  }, [site.id]);

  const exportCsv = () => {
    if (!rows?.length) return;
    const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r.data || {}))));
    const head = ['fecha', 'contacto_id'].concat(keys);
    const esc = (v) => '"' + String(v ?? '').replace(/"/g, '""') + '"';
    const csv = [head.map(esc).join(',')].concat(
      rows.map((r) => [r.created_at, r.contact_id || ''].concat(keys.map((k) => r.data?.[k] ?? '')).map(esc).join(','))
    ).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'leads-' + site.slug + '.csv';
    a.click();
  };

  return (
    <div className="bld-modal" onClick={onClose}>
      <div className="bld-modal-box wide" onClick={(e) => e.stopPropagation()}>
        <h3>Leads de {site.name}</h3>
        {rows === null ? <p>Cargando…</p> : rows.length === 0 ? (
          <p className="pm-empty">Todavía no llega ningún envío desde esta página.</p>
        ) : (
          <>
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Datos</th>
                    <th>CRM</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{String(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td>
                        {Object.entries(r.data || {}).map(([k, v]) => (
                          <div key={k}><b>{k}:</b> {String(v)}</div>
                        ))}
                      </td>
                      <td>{r.contact_id ? '✓ contacto #' + r.contact_id : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bld-modal-actions">
              <button type="button" onClick={exportCsv}>Descargar CSV</button>
              <button type="button" className="primary" onClick={onClose}>Cerrar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
